import api from 'api';
import {
  EarningsComponentType,
  NonPayableBenefitsComponentType,
  SalaryComponent,
} from 'components/SalaryComponents/types';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { useQuery } from 'react-query';
import { convertToStructureComponent } from '../utils';

type UseSalaryStructureComponentsOptions = {
  enabled?: boolean;
  onSuccess?: (data: {
    allComponents: EarningsComponentType[];
    allNonAdhocComponents: EarningsComponentType[];
    earningComponents: EarningsComponentType[];
    nonPayableBenefitComponents: EarningsComponentType[];
    fixedEarningComponents: EarningsComponentType[];
    variableEarningComponents: EarningsComponentType[];
    fixedEarningStructureComponents: SalaryStructureComponent[];
    variableEarningStructureComponents: SalaryStructureComponent[];
    nonPayableBenefitStructureComponents: SalaryStructureComponent[];
  }) => void;
}

export const useSalaryStructureComponents = (
  options: UseSalaryStructureComponentsOptions = {},
) => {
  // Fetch earning components
  const {
    data: earningsData,
    isLoading: isEarningsLoading,
    error: earningsError,
    refetch,
  } = useQuery({
    queryKey: ['salaryComponents', 'earnings'],
    queryFn: () =>
      api.salaryComponents.queries.listSalaryComponents<SalaryComponent>({
        category: 'EARNINGS_CATEGORY',
        status: 'COMPONENT_STATUS_ACTIVE',
      }),
    select: (earningsData) => {
      // Process and categorize components
      const allEarningsData = (earningsData?.payrollListSalaryComponents?.data?.salaryComponents ||
        []) as EarningsComponentType[];

      // Separate regular earnings from non-payable benefits
      const earningComponents = allEarningsData.filter(
        (component) => component.settings?.earningDetails?.payType !== 'non_payable_benefit',
      );

      const earningComponentsWithoutAdhoc = earningComponents.filter(
        (component) => component.settings?.earningDetails?.payType !== 'adhoc',
      );

      const nonPayableBenefitComponents = allEarningsData.filter(
        (component) => component.settings?.earningDetails?.payType === 'non_payable_benefit',
      ) as NonPayableBenefitsComponentType[];

      // Categorize earnings into fixed and variable
      const fixedEarningComponents = earningComponents.filter(
        (component) => component.settings?.earningDetails.payType === 'fixed',
      );

      const variableEarningComponents = earningComponents.filter(
        (component) => component.settings?.earningDetails?.payType === 'variable',
      );

      // Convert to structure components format
      const fixedEarningStructureComponents: SalaryStructureComponent[] =
        fixedEarningComponents.map((component) => convertToStructureComponent(component));

      const variableEarningStructureComponents: SalaryStructureComponent[] =
        variableEarningComponents.map((component) => convertToStructureComponent(component));

      const nonPayableBenefitStructureComponents: SalaryStructureComponent[] =
        nonPayableBenefitComponents.map((component) => convertToStructureComponent(component));

      const allComponents = [...earningComponents, ...nonPayableBenefitComponents];
      const allNonAdhocComponents = [
        ...earningComponentsWithoutAdhoc,
        ...nonPayableBenefitComponents,
      ];

      return {
        allComponents,
        allNonAdhocComponents,
        earningComponents,
        nonPayableBenefitComponents,
        fixedEarningComponents,
        variableEarningComponents,
        fixedEarningStructureComponents,
        variableEarningStructureComponents,
        nonPayableBenefitStructureComponents,
      };
    },
    ...options,
  });
  const isLoading = isEarningsLoading;
  const error = earningsError;

  return {
    allComponents: earningsData?.allComponents || [],
    allNonAdhocComponents: earningsData?.allNonAdhocComponents || [],
    earningComponents: earningsData?.earningComponents || [],
    nonPayableBenefitComponents: earningsData?.nonPayableBenefitComponents || [],
    fixedEarningComponents: earningsData?.fixedEarningComponents || [],
    variableEarningComponents: earningsData?.variableEarningComponents || [],
    fixedEarningStructureComponents: earningsData?.fixedEarningStructureComponents || [],
    variableEarningStructureComponents: earningsData?.variableEarningStructureComponents || [],
    nonPayableBenefitStructureComponents: earningsData?.nonPayableBenefitStructureComponents || [],
    isLoading,
    error,
    refetch,
  };
};